import * as React from 'react';

// Type-only imports so the `{@link Input}` and `{@link InputGroup}` JSDoc
// references below resolve.
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type Input } from './Input';
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type InputGroup } from './InputGroup';
import { cn } from './utils/cn';

/**
 * Multi-line text input that grows with its content.
 *
 * For a single-line field use {@link Input}; for textareas with addons (action
 * buttons, character counts, attachments), wrap with {@link InputGroup}.
 *
 * @example
 * <Textarea
 *   placeholder="Notes for the care team"
 *   defaultValue={existingNote}
 *   onChange={(e) => setNote(e.currentTarget.value)}
 * />
 */
function Textarea({
  className,
  size = 'md',
  ...props
}: React.ComponentProps<'textarea'> & { size?: 'md' | 'lg' }) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(
        'flex field-sizing-content w-full border border-input bg-bg-primary transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:bg-input/30 dark:disabled:bg-input/80 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40',
        size === 'lg'
          ? 'min-h-24 rounded-lg px-4 py-3 text-sm'
          : 'min-h-20 rounded-md p-2.5 text-sm',
        className,
      )}
      {...props}
    />
  );
}

export { Textarea };
